import React, { useEffect, useState } from 'react';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BatchIdService from '../../../Services/BatchIdService';
import { BATCH_ID_CATEGORY, eventGoogleAnalytics } from '../../../Utils/googleAnalytics';
import Notification from '../../Layout/Notification';
import '../../../App.scss';
import './BatchID.scss';

export default function BatchIDGenerator() {
  const theme = useTheme();
  const ACCENT = theme.tokens.accent;
  const [batchId, setBatchId] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const isMounted = React.useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleGenerateBatchId = async () => {
    try {
      setBatchId('');
      setCopied(false);
      setLoading(true);

      const result = await BatchIdService.fetchBatchId();
      const newId = result?.batchId;

      if (!newId) {
        throw new Error('Server returned no Batch ID');
      }

      if (isMounted.current) {
        setBatchId(newId);
        eventGoogleAnalytics(BATCH_ID_CATEGORY, 'Batch id generated');
        Notification.showSuccess('Batch ID generated');
      }
    } catch (err) {
      if (isMounted.current) {
        setBatchId('');
        Notification.fromError(err, { fallbackTitle: "Couldn't generate Batch ID" });
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleCopyToClipboard = () => {
    if (!batchId) return;

    navigator.clipboard.writeText(batchId).then(() => {
      setCopied(true);
      Notification.showSuccess('Batch ID copied to clipboard!');
      setTimeout(() => setCopied(false), 3000);
    });
  };

  return (
    <div id="batch-id-generator-container">
      <div className="ipxc-admin-body">
        <Grid container spacing={2} alignItems="stretch">
          <Grid size={{ xs: 12, md: 6 }} className="bid-grid-flex">
            <Card variant="outlined" className="bid-card-full">
              <CardHeader
                title="Generate Batch ID"
                subheader="Create a unique identifier for batch operations"
                titleTypographyProps={{ variant: 'h4', fontWeight: 'bold' }}
                subheaderTypographyProps={{ variant: 'body2' }}
              />
              <CardContent className="bid-card-content-grow">
                <div className="bid-display-box">
                  {loading ? (
                    <Typography variant="body2" color="text.secondary">
                      Generating Batch ID...
                    </Typography>
                  ) : batchId ? (
                    <>
                      <Typography variant="h3" className="bid-batch-id-text">
                        {batchId}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                          <IconButton
                            onClick={handleCopyToClipboard}
                            style={{
                              color: copied ? theme.tokens.success : ACCENT,
                              backgroundColor: copied
                                ? alpha(theme.tokens.success, 0.1)
                                : 'transparent',
                            }}
                          >
                            {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Click the button below to generate a new Batch ID
                    </Typography>
                  )}
                </div>

                <Stack direction="row" justifyContent="flex-end" className="bid-button-wrapper">
                  <LoadingButton
                    id="generate-button"
                    variant="contained"
                    loading={loading}
                    onClick={handleGenerateBatchId}
                    startIcon={<AutoAwesomeIcon />}
                  >
                    Generate New Batch ID
                  </LoadingButton>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} className="bid-grid-flex">
            <Card variant="outlined" className="bid-card-full">
              <CardContent className="bid-info-content">
                <Typography variant="subtitle1" gutterBottom fontWeight="bold" align="left">
                  Information
                </Typography>

                <Stack spacing={1}>
                  <Alert severity="info" icon={<InfoOutlinedIcon />} className="bid-alert-left">
                    <Typography variant="body2" align="left">
                      <strong>What is a Batch ID?</strong> A unique identifier used to group and
                      track related operations across the system.
                    </Typography>
                  </Alert>

                  <Alert severity="info" icon={<InfoOutlinedIcon />} className="bid-alert-usage">
                    <Typography variant="body2" align="left">
                      <strong>Usage:</strong> Use this Batch ID when uploading files or starting
                      batch operations to track all related records together.
                    </Typography>
                  </Alert>
                </Stack>

                <div className="bid-best-practices">
                  <Typography variant="subtitle2" className="bid-best-practices-title" align="left">
                    Best Practices
                  </Typography>
                  <ul className="bid-best-practices-list">
                    <Typography variant="body2" component="li" className="bid-list-item">
                      Generate a new Batch ID for each distinct batch operation
                    </Typography>
                    <Typography variant="body2" component="li" className="bid-list-item">
                      Copy and save the Batch ID before starting your operation
                    </Typography>
                    <Typography variant="body2" component="li" className="bid-list-item">
                      Use the same Batch ID for all files in a single batch
                    </Typography>
                    <Typography variant="body2" component="li" className="bid-list-item-last">
                      Keep a record of Batch IDs for future reference and tracking
                    </Typography>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
